import React, { useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LocalizedLink from "../LocalizedLink/LocalizedLink";
import BlogSidebar from "../BlogSidebar/BlogSidebar.js";
import { useBlog } from "../../config/BlogProvider";
import { useLanguage } from "../../context/LanguageContext";
import { ikUrl } from "../../utils/imagekit";
import {
  getTranslation,
  matchesQuery,
  estimateReadingTime,
  availableLangs,
  deriveCategories,
  formatDate,
} from "../../utils/article";

const PAGE_SIZE = 5;

const ClickHandler = () => {
  window.scrollTo(10, 0);
};

const BlogList = (props) => {
  const { articles, loading, error } = useBlog();
  const { lang } = useLanguage();
  const { t } = useTranslation(["blog", "common"]);
  const [searchParams, setSearchParams] = useSearchParams();

  const q = searchParams.get("q") || "";
  const activeCat = searchParams.get("cat") || "";
  const [visible, setVisible] = useState(PAGE_SIZE);

  const categories = useMemo(
    () => deriveCategories(articles, lang),
    [articles, lang]
  );

  const filtered = useMemo(() => {
    return (articles || []).filter((a) => {
      if (activeCat && a.category !== activeCat) return false;
      if (!matchesQuery(a, lang, q)) return false;
      return true;
    });
  }, [articles, lang, q, activeCat]);

  const setParam = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    setSearchParams(params);
    setVisible(PAGE_SIZE);
  };

  const shown = filtered.slice(0, visible);

  return (
    <section className="wpo-blog-pg-section section-padding">
      <div className="container">
        <div className="row">
          <div className={`col col-lg-8 col-12 ${props.blRight || ""}`}>
            <div className="wpo-blog-content">
              {/* Search + category filters */}
              <div className="blog-toolbar">
                <form
                  className="blog-search"
                  onSubmit={(e) => e.preventDefault()}
                >
                  <input
                    type="text"
                    className="form-control"
                    placeholder={t("blog:searchPlaceholder")}
                    value={q}
                    onChange={(e) => setParam("q", e.target.value)}
                  />
                </form>
                {categories.length > 0 && (
                  <div className="blog-cat-chips">
                    <button
                      type="button"
                      className={!activeCat ? "chip active" : "chip"}
                      onClick={() => setParam("cat", "")}
                    >
                      {t("common:all")}
                    </button>
                    {categories.map((c) => (
                      <button
                        key={c.slug}
                        type="button"
                        className={activeCat === c.slug ? "chip active" : "chip"}
                        onClick={() => setParam("cat", c.slug)}
                      >
                        {t(`blog:cat.${c.slug}`, { defaultValue: c.slug })}{" "}
                        <span>{c.count}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {loading && <p>{t("common:loading")}</p>}
              {error && <p className="blog-error">{error}</p>}

              {!loading && filtered.length === 0 && (
                <div className="blog-empty">
                  <p>{t("blog:noResults")}</p>
                </div>
              )}

              {shown.map((article) => {
                const picked = getTranslation(article, lang);
                if (!picked) return null;
                const { tr } = picked;
                const langs = availableLangs(article);
                return (
                  <div className="post format-standard-image" key={article.id}>
                    {article.coverImage && (
                      <div className="entry-media">
                        <LocalizedLink
                          onClick={ClickHandler}
                          to={`/blog/${tr.slug}`}
                        >
                          <img
                            src={ikUrl(
                              article.coverImage.url || article.coverImage.path,
                              "w-800,q-80,f-auto"
                            )}
                            alt={article.coverImage.alt || tr.title}
                            loading="lazy"
                          />
                        </LocalizedLink>
                      </div>
                    )}
                    <div className="entry-meta">
                      <ul>
                        <li>
                          <i className="fi flaticon-user"></i> {article.author}
                        </li>
                        <li>
                          <i className="fi flaticon-calendar"></i>{" "}
                          {formatDate(article.publishedAt, lang)}
                        </li>
                        <li>{t("blog:readingTime", { count: estimateReadingTime(tr) })}</li>
                        {article.category && (
                          <li>
                            {t(`blog:cat.${article.category}`, {
                              defaultValue: article.category,
                            })}
                          </li>
                        )}
                      </ul>
                    </div>
                    <div className="entry-details">
                      <h3>
                        <LocalizedLink
                          onClick={ClickHandler}
                          to={`/blog/${tr.slug}`}
                        >
                          {tr.title}
                        </LocalizedLink>
                      </h3>
                      <p>{tr.excerpt}</p>
                      {langs.length > 1 && (
                        <div className="lang-badges">
                          {langs.map((l) => (
                            <span key={l} className="lang-badge">
                              {l.toUpperCase()}
                            </span>
                          ))}
                        </div>
                      )}
                      <LocalizedLink
                        onClick={ClickHandler}
                        to={`/blog/${tr.slug}`}
                        className="read-more"
                      >
                        {t("common:readMore")}
                      </LocalizedLink>
                    </div>
                  </div>
                );
              })}

              {visible < filtered.length && (
                <div className="load-more-wrap">
                  <button
                    type="button"
                    className="theme-btn"
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                  >
                    {t("common:loadMore")}
                  </button>
                </div>
              )}
            </div>
          </div>
          <BlogSidebar blLeft={props.blLeft} />
        </div>
      </div>
    </section>
  );
};

export default BlogList;
